import { useState, useEffect, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import api from '../../lib/api';
import { FileUploadZone, type UploadedFile } from './FileUploadZone';
import type { ScrapeResult } from '../../lib/mapScrapeToQuestionnaire';
import type { QuestionnaireExtractPayload } from '../../lib/mapExtractToQuestionnaire';
import type { CompanyData } from './CompanySection';
import { getCrmStatus, type CrmConnectionInfo } from '../../lib/crmApi';

type ScrapePhase = 'idle' | 'fetching' | 'applying' | 'success' | 'error';
type ExtractPhase = 'idle' | 'extracting' | 'applying' | 'success' | 'error';

function CrmImportSection() {
  const [hasCrmFeature, setHasCrmFeature] = useState(false);
  const [crmConnections, setCrmConnections] = useState<CrmConnectionInfo[]>([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    api
      .get('/orgs/me')
      .then((res) => {
        const features: string[] = res.data.features ?? [];
        setHasCrmFeature(features.includes('crm_connect'));
        if (features.includes('crm_connect')) {
          getCrmStatus()
            .then((conns) => {
              setCrmConnections(conns);
            })
            .catch(() => {});
        }
      })
      .catch(() => {})
      .finally(() => setLoaded(true));
  }, []);

  if (!loaded) return null;

  const getConnection = (provider: string) =>
    crmConnections.find((c) => c.provider === provider && c.status === 'active');

  const crmProviders = [
    {
      id: 'salesforce',
      label: 'Salesforce',
      description: 'Pull leads, accounts, and opportunity context.',
    },
    { id: 'hubspot', label: 'HubSpot', description: 'Sync companies, deals, and CRM properties.' },
    {
      id: 'pipedrive',
      label: 'Pipedrive',
      description: 'Import deals, organizations, and contacts.',
    },
  ];

  return (
    <div className="pt-2 border-t border-green-100/80">
      <h3 className="text-sm font-semibold text-gray-900 mb-1">Import from your CRM</h3>
      <p className="text-xs text-gray-600 mb-3 max-w-3xl leading-relaxed">
        {hasCrmFeature
          ? 'Connect your CRM to import accounts, contacts, and pipeline data into personas — giving reps practice that reflects real opportunities.'
          : 'Connect Salesforce, HubSpot, or Pipedrive to import accounts, contacts, pipeline, and key fields from your CRM into this business profile — available on Pro and Enterprise plans.'}
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {crmProviders.map((prov) => {
          const conn = getConnection(prov.id);
          return (
            <div
              key={prov.id}
              className={`rounded-lg border p-4 flex flex-col gap-3 ${conn ? 'border-green-200 bg-green-50/50' : hasCrmFeature ? 'border-blue-200 bg-blue-50/30' : 'border-dashed border-gray-300 bg-gray-50/90'}`}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="font-medium text-gray-900">{prov.label}</p>
                  <p className="text-xs text-gray-500 mt-0.5">{prov.description}</p>
                </div>
                {conn ? (
                  <span className="shrink-0 text-xs font-semibold uppercase tracking-wide text-green-800 bg-green-100 px-2 py-1 rounded">
                    Connected
                  </span>
                ) : !hasCrmFeature ? (
                  <span className="shrink-0 text-xs font-semibold uppercase tracking-wide text-gray-500 bg-gray-100 px-2 py-1 rounded">
                    Pro plan
                  </span>
                ) : null}
              </div>
              {conn ? (
                <Link
                  to="/personas"
                  className="self-start px-4 py-2 rounded-md text-sm font-medium bg-green-700 text-white hover:bg-green-800"
                >
                  Generate CRM personas
                </Link>
              ) : hasCrmFeature ? (
                <Link
                  to="/settings"
                  className="self-start px-4 py-2 rounded-md text-sm font-medium bg-blue-600 text-white hover:bg-blue-700"
                >
                  Connect {prov.label}
                </Link>
              ) : (
                <Link
                  to="/billing"
                  className="self-start px-4 py-2 rounded-md text-sm font-medium border border-gray-200 bg-white text-gray-600 hover:bg-gray-50"
                >
                  Upgrade to Pro
                </Link>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export interface QuestionnaireQuickStartProps {
  data: CompanyData;
  onChange: (data: CompanyData) => void;
  questionnaireId: string;
  uploads: UploadedFile[];
  generalUploads?: UploadedFile[];
  onUploadsChange: (uploads: UploadedFile[]) => void;
  onScrapeComplete?: (scrape: ScrapeResult) => Promise<string[]>;
  onExtractComplete?: (payload: QuestionnaireExtractPayload) => Promise<string[]>;
}

function sectionLabel(section: string): string {
  return section.charAt(0).toUpperCase() + section.slice(1);
}

const EMPTY_UPLOADS: UploadedFile[] = [];

export function QuestionnaireQuickStart({
  data,
  onChange,
  questionnaireId,
  uploads,
  generalUploads = EMPTY_UPLOADS,
  onUploadsChange,
  onScrapeComplete,
  onExtractComplete,
}: QuestionnaireQuickStartProps) {
  const [phase, setPhase] = useState<ScrapePhase>('idle');
  const [scrapeError, setScrapeError] = useState('');
  const [updatedSections, setUpdatedSections] = useState<string[]>([]);

  const [extractPhase, setExtractPhase] = useState<ExtractPhase>('idle');
  const [extractError, setExtractError] = useState('');
  const [extractUpdatedSections, setExtractUpdatedSections] = useState<string[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set());

  const companyIdsKey = useMemo(
    () =>
      uploads
        .map((u) => u.id)
        .sort()
        .join(','),
    [uploads],
  );
  const generalIdsKey = useMemo(
    () =>
      generalUploads
        .map((u) => u.id)
        .sort()
        .join(','),
    [generalUploads],
  );

  useEffect(() => {
    const companySet = new Set(uploads.map((u) => u.id));
    const generalSet = new Set(generalUploads.map((u) => u.id));

    setSelectedIds((prev) => {
      const next = new Set<string>();
      prev.forEach((id) => {
        if (companySet.has(id) || generalSet.has(id)) next.add(id);
      });
      uploads.forEach((u) => next.add(u.id));
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- sync selection when upload id sets change
  }, [companyIdsKey, generalIdsKey]);

  useEffect(() => {
    if (phase !== 'success') return;
    const timer = setTimeout(() => setPhase('idle'), 5000);
    return () => clearTimeout(timer);
  }, [phase]);

  useEffect(() => {
    if (extractPhase !== 'success') return;
    const timer = setTimeout(() => setExtractPhase('idle'), 8000);
    return () => clearTimeout(timer);
  }, [extractPhase]);

  const handleScrape = async () => {
    if (!data.website) return;
    setPhase('fetching');
    setScrapeError('');
    setUpdatedSections([]);
    try {
      const res = await api.post('/questionnaire/scrape', { url: data.website });
      const scraped: ScrapeResult = res.data;
      if (scraped.resolvedUrl?.trim()) {
        onChange({ ...data, website: scraped.resolvedUrl.trim() });
      }

      setPhase('applying');
      const sections = onScrapeComplete ? await onScrapeComplete(scraped) : [];
      setUpdatedSections(sections);
      setPhase('success');
    } catch {
      setScrapeError('Failed to scrape website. Please fill in details manually.');
      setPhase('error');
    }
  };

  const toggleUploadId = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const handleExtractFromDocuments = async () => {
    const uploadIds = [...selectedIds];
    if (uploadIds.length === 0 || !onExtractComplete) return;

    setExtractPhase('extracting');
    setExtractError('');
    setExtractUpdatedSections([]);
    try {
      const res = await api.post('/questionnaire/extract', { uploadIds });
      const payload = res.data as QuestionnaireExtractPayload;

      setExtractPhase('applying');
      const sections = await onExtractComplete(payload);
      setExtractUpdatedSections(sections);
      setExtractPhase('success');
    } catch {
      setExtractError(
        'Could not extract data from the selected files. Try PDF, Word, or Markdown.',
      );
      setExtractPhase('error');
    }
  };

  const update = (field: keyof CompanyData, value: string) => {
    onChange({ ...data, [field]: value });
  };

  const busy = phase === 'fetching' || phase === 'applying';
  const extractBusy = extractPhase === 'extracting' || extractPhase === 'applying';
  const hasAnyUpload = uploads.length > 0 || generalUploads.length > 0;
  const canExtract = hasAnyUpload && selectedIds.size > 0 && onExtractComplete && !extractBusy;

  return (
    <div className="space-y-6">
      <div id="questionnaire-company-website">
        <h3 className="text-sm font-semibold text-gray-900 mb-2">Website + auto-fill</h3>
        <label className="block text-sm font-medium text-gray-700 mb-1">Company website</label>
        <div className="flex flex-col sm:flex-row gap-2">
          <input
            type="text"
            inputMode="url"
            autoComplete="url"
            value={data.website}
            onChange={(e) => update('website', e.target.value)}
            placeholder="https://yourcompany.com"
            className="flex-1 min-w-0 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
          />
          <button
            type="button"
            onClick={handleScrape}
            disabled={busy || !data.website}
            className="shrink-0 px-4 py-2 bg-green-700 text-white rounded-md text-sm font-medium hover:bg-green-800 disabled:opacity-50"
          >
            {busy ? (phase === 'fetching' ? 'Scraping...' : 'Applying...') : 'Auto-fill'}
          </button>
        </div>

        <div aria-live="polite" role="status" className="mt-2">
          {phase === 'fetching' && (
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <div className="h-1.5 flex-1 max-w-xs bg-gray-200 rounded-full overflow-hidden">
                <div className="h-full bg-green-500 rounded-full animate-indeterminate" />
              </div>
              <span>Fetching website data...</span>
            </div>
          )}
          {phase === 'applying' && (
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <div className="h-1.5 w-4 bg-green-500 rounded-full animate-pulse" />
              <span>Applying data to business profile...</span>
            </div>
          )}
          {phase === 'success' && (
            <p className="text-sm text-green-700 flex items-center gap-1">
              <svg className="w-4 h-4 shrink-0" viewBox="0 0 20 20" fill="currentColor">
                <path
                  fillRule="evenodd"
                  d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                  clipRule="evenodd"
                />
              </svg>
              {updatedSections.length > 0
                ? `Auto-filled: ${updatedSections.map(sectionLabel).join(', ')}`
                : 'Scrape complete — no new data found'}
            </p>
          )}
          {phase === 'error' && <p className="text-sm text-red-600">{scrapeError}</p>}
        </div>
      </div>

      {onExtractComplete && (
        <div
          id="questionnaire-fill-from-documents"
          className="rounded-lg border border-gray-200 bg-white/80 p-4 sm:p-5 space-y-4"
        >
          <div>
            <h3 className="text-sm font-semibold text-gray-900">Documents</h3>
            <p className="text-xs text-gray-600 mt-1 max-w-3xl leading-relaxed">
              Drop your files in the zone below—pitch decks, PDFs, Word docs, markdown, or internal
              notes. Select which files to use, then run extract; we map content into Company,
              Product, Sales, Competitors, and Team wherever the documents have useful information.
              You can also include uploads from{' '}
              <span className="font-medium text-gray-700">Additional documents</span> at the bottom
              of this page (they appear in the second list when present).
            </p>
          </div>

          <FileUploadZone
            questionnaireId={questionnaireId}
            section="company"
            uploads={uploads}
            onUploadsChange={onUploadsChange}
          />

          {uploads.length > 0 && (
            <div className="space-y-1">
              <p className="text-xs font-medium text-gray-600">Dropped here</p>
              <ul className="space-y-1">
                {uploads.map((file) => (
                  <li key={file.id} className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      id={`extract-${file.id}`}
                      checked={selectedIds.has(file.id)}
                      onChange={() => toggleUploadId(file.id)}
                      className="rounded border-gray-300"
                    />
                    <label htmlFor={`extract-${file.id}`} className="truncate cursor-pointer">
                      {file.filename}
                    </label>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {generalUploads.length > 0 && (
            <div className="space-y-1">
              <p className="text-xs font-medium text-gray-600">
                Additional documents (bottom of page)
              </p>
              <ul className="space-y-1">
                {generalUploads.map((file) => (
                  <li key={file.id} className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      id={`extract-gen-${file.id}`}
                      checked={selectedIds.has(file.id)}
                      onChange={() => toggleUploadId(file.id)}
                      className="rounded border-gray-300"
                    />
                    <label htmlFor={`extract-gen-${file.id}`} className="truncate cursor-pointer">
                      {file.filename}
                    </label>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div>
            <button
              type="button"
              onClick={handleExtractFromDocuments}
              disabled={!canExtract || busy}
              className="px-4 py-2 bg-white border border-green-700 text-green-800 rounded-md text-sm font-medium hover:bg-green-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {extractBusy
                ? extractPhase === 'extracting'
                  ? 'Reading documents...'
                  : 'Applying...'
                : 'Extract & fill business profile'}
            </button>
          </div>

          <div aria-live="polite" role="status">
            {extractPhase === 'extracting' && (
              <p className="text-sm text-gray-600">
                Extracting structured data (this may take a moment)...
              </p>
            )}
            {extractPhase === 'applying' && (
              <p className="text-sm text-gray-600">Applying to business profile...</p>
            )}
            {extractPhase === 'success' && (
              <p className="text-sm text-green-700">
                {extractUpdatedSections.length > 0
                  ? `Updated: ${extractUpdatedSections.map(sectionLabel).join(', ')}`
                  : 'Extraction finished — no new fields matched your business profile schema.'}
              </p>
            )}
            {extractPhase === 'error' && <p className="text-sm text-red-600">{extractError}</p>}
          </div>
        </div>
      )}

      <CrmImportSection />
    </div>
  );
}
